import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from "react-i18next";
import { ConfigContext } from '../../../../contexts/config-context';
import { UserContext, profilePhoto } from '../../../../contexts/userContext';
import CountryAPI from '../../../../axios/country';
import UserAPI from '../../../../axios/user';
import MiscAPI from '../../../../axios/misc';
import { Check, Field, SelectDropdown, Toggle } from '../panel-ui';

const MAX_PHOTO_BYTES = 5 * 1024 * 1024;

const EMPTY_PROFILE = {
    email: "",
    first_name: "",
    last_name: "",
    phone: "",
    mobile: "",
    country: "RS",
    city: "",
    zip_code: "",
    address: "",
    user_type: 1,
    send_messages: false,
    send_notifications: false,
};

const CONTACT_FIELDS = [
    { name: 'first_name', label: 'first_name' },
    { name: 'last_name', label: 'last_name' },
    { name: 'email', label: 'mail', type: 'email', placeholder: 'ime@primer.com' },
    { name: 'phone', label: 'phone', type: 'tel', placeholder: '011 123 4567' },
    { name: 'mobile', label: 'mobile', type: 'tel', placeholder: '06x xxx xxxx' },
];

const ADDRESS_FIELDS = [
    { name: 'city', label: 'city', placeholder: 'Beograd' },
    { name: 'zip_code', label: 'postal', placeholder: '11000' },
    { name: 'address', label: 'address' },
];

// how long the bottom bar keeps saying "saved" before it hides
const SAVED_NOTICE_MS = 2500;

const PREFS = [
    { name: 'send_messages', title: 'pref_messages' },
    { name: 'send_notifications', title: 'pref_notifications' },
];

export const UserData = () => {
    const { api } = useContext(ConfigContext);
    const { user, setUser } = useContext(UserContext);
    const { t, i18n } = useTranslation(["profile"]);
    const lang = i18n.language;

    const [countries, setCountries] = useState([]);
    const [userTypes, setUserTypes] = useState([]);
    const [profile, setProfile] = useState(EMPTY_PROFILE);
    // photo stored on the server, and a newly picked file that is not saved yet
    const [savedPhoto, setSavedPhoto] = useState(null);
    const [newPhoto, setNewPhoto] = useState(null);
    const [photoMenuOpen, setPhotoMenuOpen] = useState(false);
    const [status, setStatus] = useState(null);
    const photoRef = useRef(null);
    const fileRef = useRef(null);
    // the profile as last stored on the server, without unsaved edits
    const savedProfile = useRef(EMPTY_PROFILE);

    const loadProfile = (data) => {
        const { photo, ...rest } = data;
        // filled with the defaults the form starts from, so a field the server leaves out does not count as an edit
        savedProfile.current = { ...EMPTY_PROFILE, ...rest };
        setProfile(prev => ({ ...prev, ...rest }));
        setSavedPhoto(profilePhoto(photo));
        setNewPhoto(null);
    };

    // the header shows the name and photo from the stored user
    const updateUser = (changes) => {
        const updated = { ...user, ...changes };
        localStorage.setItem("user", JSON.stringify(updated));
        setUser(updated);
    };

    useEffect(() => {
        CountryAPI.getListOfCountries().then(res => setCountries(res.data)).catch(err => console.log(err));
        UserAPI.getProfileData().then(res => loadProfile(res.data)).catch(err => console.log(err));
    }, []);

    useEffect(() => {
        MiscAPI.getUserTypeOptions(lang).then(res => setUserTypes(res.data)).catch(err => console.log(err));
    }, [lang]);

    useEffect(() => {
        const close = (e) => {
            if (photoRef.current && !photoRef.current.contains(e.target)) setPhotoMenuOpen(false);
        };
        document.addEventListener('mousedown', close);
        return () => document.removeEventListener('mousedown', close);
    }, []);

    // a saved notice fades on its own; the bottom bar hides with it
    useEffect(() => {
        if (status !== 'saved') return;
        const timer = setTimeout(() => setStatus(current => current === 'saved' ? null : current), SAVED_NOTICE_MS);
        return () => clearTimeout(timer);
    }, [status]);

    const dirty = !!newPhoto || Object.keys(EMPTY_PROFILE)
        .some(key => String(profile[key] ?? '') !== String(savedProfile.current[key] ?? ''));

    const photoUrl = newPhoto ? newPhoto.url
        : savedPhoto ? `${api.host}:${api.port}${savedPhoto}` : null;

    const change = (name, value) => {
        setProfile(prev => ({ ...prev, [name]: value }));
        setStatus(null);
    };

    const pickPhoto = (e) => {
        const file = e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (file.size > MAX_PHOTO_BYTES) {
            setStatus('photo_too_big');
            return;
        }
        setNewPhoto({ file, url: URL.createObjectURL(file) });
        setStatus(null);
    };

    // removing takes effect right away, without touching the unsaved edits of the other fields
    const removePhoto = () => {
        setNewPhoto(null);
        setPhotoMenuOpen(false);
        setStatus(null);
        if (!savedPhoto) return;

        // an empty photo resets it to the placeholder
        UserAPI.saveProfile({ ...savedProfile.current, photo: '' }).then(() => {
            setSavedPhoto(null);
            updateUser({ photo: null });
        }).catch((err) => {
            console.log(err);
            setStatus('error');
        });
    };

    const save = () => {
        const data = { ...profile };
        // no photo key keeps the current one
        if (newPhoto) data.photo = newPhoto.file;

        UserAPI.saveProfile(data).then(() => {
            setStatus('saved');
            return UserAPI.getProfileData();
        }).then((res) => {
            loadProfile(res.data);
            updateUser({ first_name: res.data.first_name, last_name: res.data.last_name, photo: profilePhoto(res.data.photo) });
        }).catch((err) => {
            console.log(err);
            setStatus('error');
        });
    };

    const fullName = `${profile.first_name} ${profile.last_name}`.trim();
    const initials = `${profile.first_name?.[0] || ''}${profile.last_name?.[0] || ''}`.toUpperCase();
    const userType = userTypes.find(o => String(o.id) === String(profile.user_type))?.label;
    const statusText = status && (
        status === 'saved'
            ? <span className='hs-panel-status ok'><Check />{t('changes_saved')}</span>
            : <span className='hs-panel-status err'>{t(status === 'error' ? 'change_profile_err' : 'photo_too_big')}</span>
    );

    const field = ({ name, label, type = 'text', placeholder = '' }) => (
        <Field key={name} label={t(label)} type={type} placeholder={placeholder}
            value={profile[name] || ''} onChange={(e) => change(name, e.target.value)} />
    );

    return (
        <section className='hs hs-panel-page'>

            <div className='hs-panel-card hs-panel-profile'>
                <div className='hs-panel-avatar-wrap' ref={photoRef}>
                    <button className={`hs-panel-avatar ${photoMenuOpen ? 'open' : ''}`} aria-label={t('edit_photo')}
                        onClick={() => setPhotoMenuOpen(!photoMenuOpen)}>
                        {photoUrl
                            ? <span className='hs-panel-avatar-photo' style={{ backgroundImage: `url(${photoUrl})` }}></span>
                            : <span>{initials}</span>}
                        <span className='hs-panel-avatar-overlay'>
                            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 8a2 2 0 0 1 2-2h2l1.5-2h7L17 6h2a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" /><circle cx="12" cy="13" r="3.5" /></svg>
                        </span>
                    </button>
                    <span className='hs-panel-avatar-edit'>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></svg>
                    </span>
                    <input ref={fileRef} type='file' accept='image/jpeg,image/png' hidden onChange={pickPhoto} />
                    {photoMenuOpen && (
                        <div className='hs-panel-pop hs-panel-avatar-pop'>
                            <button className='hs-panel-pop-item' onClick={() => { setPhotoMenuOpen(false); fileRef.current.click(); }}>
                                <span className='hs-panel-pop-icon'>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4M17 8l-5-5-5 5M12 3v12" /></svg>
                                </span>
                                <span className='hs-panel-pop-text'>
                                    <span className='hs-panel-pop-label'>{t(photoUrl ? 'change_photo' : 'upload_photo')}</span>
                                    <span className='hs-panel-pop-sub'>{t('photo_hint')}</span>
                                </span>
                            </button>
                            {photoUrl && (
                                <button className='hs-panel-pop-item danger' onClick={removePhoto}>
                                    <span className='hs-panel-pop-icon'>
                                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 10v6M14 10v6" /></svg>
                                    </span>
                                    <span className='hs-panel-pop-label'>{t('delete_photo')}</span>
                                </button>
                            )}
                        </div>
                    )}
                </div>
                <div className='hs-panel-profile-info'>
                    <span className='hs-panel-profile-name'>{fullName}</span>
                    <span className='hs-panel-profile-meta'>
                        {profile.email}
                        {userType && <span className='hs-panel-badge blue'>{userType}</span>}
                    </span>
                </div>
            </div>

            <div className='hs-panel-card hs-panel-form'>
                <h2>{t('subtitle_1')}</h2>
                <div className='hs-panel-fields'>
                    {CONTACT_FIELDS.map(field)}
                </div>
            </div>

            <div className='hs-panel-card hs-panel-form'>
                <h2>{t('address')}</h2>
                <div className='hs-panel-fields'>
                    <SelectDropdown label={t('country')} value={profile.country} options={countries}
                        onChange={(id) => change('country', id)} />
                    {ADDRESS_FIELDS.map(field)}
                </div>
            </div>

            <div className='hs-panel-card hs-panel-rows'>
                {PREFS.map(pref => (
                    <div className='hs-panel-row' key={pref.name}>
                        <span className='hs-panel-row-text'>
                            <span className='hs-panel-row-title'>{t(pref.title)}</span>
                            <span className='hs-panel-row-sub'>{t(pref.name)}</span>
                        </span>
                        <Toggle on={!!profile[pref.name]} label={t(pref.title)} onChange={(on) => change(pref.name, on)} />
                    </div>
                ))}
            </div>

            {/* shows up once something is changed, so saving is never a scroll away */}
            {(dirty || status) &&
                <div className='hs-panel-savebar'>
                    {dirty
                        ? <>
                            <span className='hs-panel-savebar-text'>{status && status !== 'saved' ? statusText : t('unsaved_changes')}</span>
                            <button className='hs-panel-primary' onClick={save}>{t('save')}</button>
                        </>
                        : statusText}
                </div>}
        </section>
    );
};
